import { useTextClamp } from '@/hooks'

import { cleanDirtyTableContent } from '@/components/helpers'
import { MarkdownWrapper } from '@/components/MarkdownWrapper'
import { Button } from '@repo/ui/button'
import { useT } from '@repo/ui/hooks'
import { cn } from '@repo/ui/utils'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { ComponentProps, ReactNode, useMemo, useState } from 'react'
import Markdown from 'react-markdown'

const getLineClampClass = (lines: number): string => `line-clamp-${lines}`

export function ClampedContent({
  buttonProps,
  content,
  contentComp,
  lines,
}: {
  content: string
  contentComp?: ReactNode
} & ClampedContentWithoutTableProps) {
  if (contentComp) {
    return <div className="whitespace-pre-wrap text-pretty">{contentComp}</div>
  }

  const cleanContent = cleanDirtyTableContent(content)
  // Check if the content contains a markdown table
  const hasMarkdownTable = useMemo(
    () => cleanContent.match(/^[ \t]*\|.*\|[ \t]*\n[ \t]*\|[ \t\-:|]+\|/m),
    [cleanContent],
  )

  if (hasMarkdownTable) {
    return <ContentWithTable content={cleanContent} buttonProps={buttonProps} />
  }
  return (
    <ClampedContentWithoutTable
      content={content}
      lines={lines}
      buttonProps={buttonProps}
    />
  )
}

function ContentWithTable({
  content,
  buttonProps,
}: {
  content: string
  buttonProps?: ComponentProps<typeof Button>
}) {
  const { tc } = useT()
  const [isExpanded, setIsExpanded] = useState(false)
  // Custom table renderer for Markdown

  const ChevronIcon = isExpanded ? ChevronUp : ChevronDown
  const buttonText = isExpanded
    ? tc('seeLess', { cfl: true })
    : tc('seeMore', { cfl: true })

  return (
    <div className="flex flex-col gap-2">
      <div className={isExpanded ? '' : 'max-h-64 overflow-hidden'}>
        <MarkdownWrapper content={content} />
      </div>
      <div className="flex items-center justify-start">
        <Button
          size="sm"
          {...buttonProps}
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
          aria-label={buttonText}
        >
          {buttonText} <ChevronIcon className="size-4" />
        </Button>
      </div>
    </div>
  )
}

type ClampedContentWithoutTableProps = {
  buttonProps?: ComponentProps<typeof Button>
  lines: number
}
function ClampedContentWithoutTable({
  content,
  lines,
  buttonProps,
}: ClampedContentWithoutTableProps & { content: string }) {
  const { tc } = useT()
  const { isExpanded, isClamped, textRef, toggleExpanded } =
    useTextClamp<HTMLParagraphElement>({
      lines,
      dependency: content,
    })

  const lineClampClass = useMemo(() => getLineClampClass(lines), [lines])
  const ChevronIcon = isExpanded ? ChevronUp : ChevronDown
  const buttonText = isExpanded
    ? tc('seeLess', { cfl: true })
    : tc('seeMore', { cfl: true })
  return (
    <>
      <div
        ref={textRef}
        className={cn(isExpanded ? 'line-clamp-none' : lineClampClass)}
      >
        <Markdown className="custom-markdown-styles whitespace-pre-wrap text-pretty">
          {content}
        </Markdown>
      </div>
      {isClamped && (
        <div className="flex items-center justify-start">
          <Button
            size="sm"
            {...buttonProps}
            onClick={toggleExpanded}
            aria-expanded={isExpanded}
            aria-label={buttonText}
          >
            {buttonText} <ChevronIcon className="size-4" />
          </Button>
        </div>
      )}
    </>
  )
}
